import { useState, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  type TireInstallation,
  type CreatePerformanceLogRequest,
  type TireCondition,
} from 'types/tire-installation.types';
import TireInstallationHandler from 'handlers/tire-installation.handler';
import { useAppSelector } from 'store/hook';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertDisplayTimeToUtc } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

interface CreateInspectionModalProps {
  open: boolean;
  onClose: () => void;
  installation: TireInstallation;
  onSuccess: () => void;
}

const tireConditions: TireCondition[] = [
  'GOOD',
  'WATCH',
  'REPAIRABLE',
  'SCRAP',
];

export const CreateInspectionModal = ({
  open,
  onClose,
  installation,
  onSuccess,
}: CreateInspectionModalProps) => {
  const { t } = useTranslation();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  // Get current date and time in customer's timezone for datetime-local input
  const getCurrentDateTime = () => {
    const now = dayjs().tz(timezoneBasedOnDisplayMode);
    return now.format('YYYY-MM-DDTHH:mm');
  };

  const [formData, setFormData] = useState<CreatePerformanceLogRequest>({
    installationId: installation.installationId,
    assetId: installation.assetId,
    timestamp: getCurrentDateTime(),
    measuredAirPressure: '',
    averageTreadDepth: '',
    totalDistanceLog: '',
    totalEngineHoursLog: '',
    damageReported: false,
    sideWallDamage: false,
    unevenTreadWear: false,
    currentTireCondition: 'GOOD',
    inspectionNotes: '',
    inspectionImage: undefined,
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);

  const tireInstallationHandler = new TireInstallationHandler();

  const asset = assets.find((a) => a.assetId === installation.assetId);
  const assetName = asset ? asset.bumperNumber : 'Unknown Asset';

  const handleInputChange = (
    field: keyof CreatePerformanceLogRequest,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData((prev) => ({
        ...prev,
        inspectionImage: file,
      }));

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFormData((prev) => ({
      ...prev,
      inspectionImage: undefined,
    }));

    if (filePreview) {
      URL.revokeObjectURL(filePreview);
      setFilePreview(null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.measuredAirPressure || !formData.averageTreadDepth) {
      setError('Air pressure and tread depth are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert timestamp to UTC before sending
      const dataToSend = {
        ...formData,
        timestamp: convertDisplayTimeToUtc(
          formData.timestamp,
          'configured',
          timezoneBasedOnDisplayMode
        ).toISOString(),
        customerId,
      };

      const success = await tireInstallationHandler.createPerformanceLog(
        dataToSend
      );

      if (success) {
        onSuccess();
        handleClose();
      } else {
        // Error already shown by handler's toast notification
        setIsSubmitting(false);
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create inspection. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }
    setSelectedFile(null);
    setFilePreview(null);
    setError(null);
    setFormData({
      installationId: installation.installationId,
      assetId: installation.assetId,
      timestamp: new Date().toISOString(),
      measuredAirPressure: '',
      averageTreadDepth: '',
      totalDistanceLog: '',
      totalEngineHoursLog: '',
      damageReported: false,
      sideWallDamage: false,
      unevenTreadWear: false,
      currentTireCondition: 'GOOD',
      inspectionNotes: '',
      inspectionImage: undefined,
    });
    onClose();
  };

  const getFileType = (file: File) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type === 'application/pdf') return 'pdf';
    return 'unknown';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: 800,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            outline: 'none',
            color: 'text.primary',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" component="h2">
              {t('create_new_inspection')}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Installation Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('installation_details')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('tire_serial_number')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {installation.tireSerialNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('asset')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {assetName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('position')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {installation.position}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('odometer_at_install')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {installation.odometerAtInstall || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('engine_hours_at_install')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {installation.engineHoursAtInstall || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('current_condition')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {installation.latestPerformanceLog
                        ?.currentTireCondition ?? '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Form Fields */}
            <Grid container spacing={3}>
              {/* Inspection Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('inspection_date_time')}
                  type="datetime-local"
                  value={formData.timestamp}
                  onChange={(e) => {
                    handleInputChange('timestamp', e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Tire Condition */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('tire_condition_2')}</InputLabel>
                  <Select
                    value={formData.currentTireCondition}
                    label={t('tire_condition_2')}
                    onChange={(e) => {
                      handleInputChange('currentTireCondition', e.target.value);
                    }}
                  >
                    {tireConditions.map((condition) => (
                      <MenuItem key={condition} value={condition}>
                        {condition}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Air Pressure */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('air_pressure_psi')}
                  type="number"
                  value={formData.measuredAirPressure}
                  onChange={(e) => {
                    handleInputChange('measuredAirPressure', e.target.value);
                  }}
                  required
                />
              </Grid>

              {/* Tread Depth */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('tread_depth_mm')}
                  type="number"
                  value={formData.averageTreadDepth}
                  onChange={(e) => {
                    handleInputChange('averageTreadDepth', e.target.value);
                  }}
                  required
                />
              </Grid>

              {/* Total Distance */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('total_distance_2')}
                  type="number"
                  value={formData.totalDistanceLog}
                  onChange={(e) => {
                    handleInputChange('totalDistanceLog', e.target.value);
                  }}
                />
              </Grid>

              {/* Engine Hours */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('engine_hours')}
                  type="number"
                  value={formData.totalEngineHoursLog}
                  onChange={(e) => {
                    handleInputChange('totalEngineHoursLog', e.target.value);
                  }}
                />
              </Grid>

              {/* Damage Checkboxes */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('damage_assessment')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.damageReported}
                          onChange={(e) => {
                            handleInputChange(
                              'damageReported',
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label={t('damage_reported')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.sideWallDamage}
                          onChange={(e) => {
                            handleInputChange(
                              'sideWallDamage',
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label={t('sidewall_damage')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.unevenTreadWear}
                          onChange={(e) => {
                            handleInputChange(
                              'unevenTreadWear',
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label={t('uneven_tread_wear')}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Inspection Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('inspection_notes')}
                  multiline
                  rows={3}
                  value={formData.inspectionNotes}
                  onChange={(e) => {
                    handleInputChange('inspectionNotes', e.target.value);
                  }}
                  placeholder={t(
                    'enter_any_additional_notes_about_the_inspection'
                  )}
                />
              </Grid>

              {/* File Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('inspection_media_optional')}
                </Typography>

                {!selectedFile ? (
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('click_to_upload_image_or_pdf')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('supports_jpg_png_pdf_max_10mb')}
                    </Typography>
                  </Box>
                ) : (
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(selectedFile.size)} •{' '}
                            {getFileType(selectedFile).toUpperCase()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {filePreview && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (getFileType(selectedFile) === 'pdf') {
                                  window.open(filePreview, '_blank');
                                } else {
                                  setIsPreviewOpen(true);
                                }
                              }}
                              sx={{ color: 'primary.main' }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={handleRemoveFile}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Grid>
            </Grid>

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !formData.measuredAirPressure ||
                  !formData.averageTreadDepth
                }
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Creating...' : 'Create Inspection'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Image Preview Modal */}
      {isPreviewOpen &&
        filePreview &&
        selectedFile &&
        getFileType(selectedFile) === 'image' && (
          <Modal
            open={isPreviewOpen}
            onClose={() => {
              setIsPreviewOpen(false);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                maxWidth: '90vw',
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                outline: 'none',
                p: 2,
              }}
            >
              <IconButton
                onClick={() => {
                  setIsPreviewOpen(false);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={filePreview}
                alt={t('preview')}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Modal>
        )}
    </>
  );
};
